// Agent Chat Page - Chat interface for LangGraph agent

import { useState, useEffect, useRef, useCallback } from 'react';
import { useGuruWidgetViewContext } from '../hooks/useGuruWidgetViewContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button, StatusBadge } from '../components/common';
import { HistoryModal } from '../components/chat';
import { agentService, formatCost, formatLatency, formatTokens } from '../services/agent';
import type {
  ChatMessage,
  ConversationSummary,
  ToolCallDetail,
  LLMCallDetail,
  AgentResponse,
  ToolInfo,
  MCPServerInfo,
  AgentDefinition,
  AgentUpdateRequest,
  HistoryItem,
} from '../types/agent';
import { MODEL_OPTIONS, getMCPServerUrls } from '../types/agent';
import { formatDate } from '../utils/dateFormatters';
import {
  SendIcon,
  SpinnerIcon,
  ChatIcon,
  TrashIcon,
  PlusIcon,
  ToolIcon,
  SettingsIcon,
  RobotIcon,
  SaveIcon,
  EditIcon,
  CloseIcon,
  CopyIcon,
  CheckIcon,
  ExpandIcon,
} from '../components/icons';
import styles from './AgentChatPage.module.css';

// Configuration mode type
type ConfigMode = 'agent' | 'manual';

// ===================
// Table Modal
// ===================

interface TableModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

function TableModal({ isOpen, onClose, children }: TableModalProps) {
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className={styles.tableModalOverlay} onClick={onClose}>
      <div className={styles.tableModalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.tableModalClose} onClick={onClose} aria-label="Close">
          <CloseIcon />
        </button>
        {children}
      </div>
    </div>
  );
}

// ===================
// Message Bubble
// ===================

interface MessageBubbleProps {
  message: ChatMessage;
  messageIndex: number;
  messages: ChatMessage[];
  isLoading?: boolean;
  onOpenHistory: (messageIndex: number) => void;
  onOpenToolHistory: (historyIndex: number) => void;
}

function MessageBubble({ message, messageIndex, messages, isLoading, onOpenHistory, onOpenToolHistory }: MessageBubbleProps) {
  const isUser = message.role === 'user';
  const toolCalls = message.metadata?.tool_calls || [];
  const [tableModalContent, setTableModalContent] = useState<React.ReactNode | null>(null);

  // Calculate the history index for a specific tool call
  const getHistoryIndexForTool = (toolIndex: number): number => {
    let count = 0;
    // Count all history items from previous messages
    for (let i = 0; i < messageIndex; i++) {
      count += 1; // The message itself
      const msgToolCalls = messages[i].metadata?.tool_calls || [];
      msgToolCalls.forEach((tc: ToolCallDetail) => {
        count += 1; // tool_call item
        if (tc.result) count += 1; // tool_result item
      });
    }
    // Add 1 for current message
    count += 1;
    // Count up to the requested tool
    for (let t = 0; t < toolIndex; t++) {
      count += 1; // tool_call item
      if (toolCalls[t].result) count += 1; // tool_result item
    }
    return count; // This is the index of the tool_call item
  };

  // Custom table component that wraps tables in a clickable container
  const ClickableTable = useCallback(({ children, ...props }: React.HTMLAttributes<HTMLTableElement>) => {
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      // Clone the table content for the modal
      setTableModalContent(
        <table {...props}>{children}</table>
      );
    };

    return (
      <div className={styles.tableWrapper} onClick={handleClick} title="Click to expand table">
        <table {...props}>{children}</table>
        <div className={styles.tableExpandIcon}>
          <ExpandIcon />
        </div>
      </div>
    );
  }, []);

  return (
    <div className={`${styles.messageBubble} ${isUser ? styles.userMessage : styles.assistantMessage}`}>
      <div
        className={`${styles.messageContent} ${styles.clickableMessage}`}
        onClick={() => !isLoading && onOpenHistory(messageIndex)}
        title="Click to view conversation history"
      >
        {isLoading ? (
          <div className={styles.loadingMessage}>
            <SpinnerIcon className={styles.spinner} />
            <span>Thinking...</span>
          </div>
        ) : isUser ? (
          <p className={styles.messageText}>{message.content}</p>
        ) : (
          <div className={styles.markdownContent}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                table: ClickableTable,
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        )}
      </div>

      {/* Table Modal */}
      <TableModal isOpen={tableModalContent !== null} onClose={() => setTableModalContent(null)}>
        {tableModalContent}
      </TableModal>

      {/* Tool call badges */}
      {toolCalls.length > 0 && (
        <div className={styles.toolCalls}>
          {toolCalls.map((tc: ToolCallDetail, toolIndex: number) => (
            <button
              key={tc.id}
              className={styles.toolCallBadge}
              onClick={(e) => { e.stopPropagation(); onOpenToolHistory(getHistoryIndexForTool(toolIndex)); }}
              title={`View ${tc.tool_name} details`}
            >
              <ToolIcon />
              <span className={styles.toolName}>{tc.tool_name}</span>
              {tc.status === 'success' && (
                <StatusBadge variant="success">done</StatusBadge>
              )}
              {tc.status === 'error' && (
                <StatusBadge variant="error">error</StatusBadge>
              )}
              {tc.duration_ms && (
                <span className={styles.toolDuration}>{formatLatency(tc.duration_ms)}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Metadata for assistant messages */}
      {!isUser && !isLoading && message.metadata && (
        <div className={styles.messageMeta}>
          {message.metadata.tokens_used && (
            <span>{formatTokens(message.metadata.tokens_used)} tokens</span>
          )}
          {message.metadata.cost_usd && (
            <span>{formatCost(message.metadata.cost_usd)}</span>
          )}
          {message.metadata.latency_ms && (
            <span>{formatLatency(message.metadata.latency_ms)}</span>
          )}
        </div>
      )}
    </div>
  );
}

// ===================
// Sidebar Tab Types
// ===================

type SidebarTab = 'config' | 'conversations' | 'tools';

// ===================
// Main Component
// ===================

export function AgentChatPage() {
  // State
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);

  // Configuration state
  const [configMode, setConfigMode] = useState<ConfigMode>('agent');
  const [agents, setAgents] = useState<AgentDefinition[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<AgentDefinition | null>(null);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [selectedModel, setSelectedModel] = useState(MODEL_OPTIONS[0]);

  // Tell the Guru feedback widget which AI conversation is on screen.
  useGuruWidgetViewContext('agent_chat', {
    copilot: 'agent-chat',
    conversation_id: conversationId ?? undefined,
    agent_name: configMode === 'agent' ? (selectedAgent?.name ?? undefined) : undefined,
    model_id: selectedModel.model,
  });
  const [mcpServers, setMcpServers] = useState<MCPServerInfo[]>([]);
  const [enabledServers, setEnabledServers] = useState<Set<string>>(new Set());
  const [isLoadingServers, setIsLoadingServers] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState<string>('');
  const [isSystemPromptModalOpen, setIsSystemPromptModalOpen] = useState(false);
  const [tempSystemPrompt, setTempSystemPrompt] = useState<string>('');

  // Sidebar state
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>('config');
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [tools, setTools] = useState<ToolInfo[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [isLoadingTools, setIsLoadingTools] = useState(false);

  // Agent configuration editing state
  const [isSavingAgent, setIsSavingAgent] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [agentEnabledServers, setAgentEnabledServers] = useState<Set<string>>(new Set());

  // History modal state
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [highlightedHistoryIndex, setHighlightedHistoryIndex] = useState<number | null>(null);
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);

  // Copy conversation ID state
  const [copiedConversationId, setCopiedConversationId] = useState(false);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef(true);

  // Track if user is near bottom to decide auto-scroll behavior
  const handleMessagesScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Consider "near bottom" if within 100px of the bottom
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    shouldAutoScrollRef.current = isNearBottom;
  }, []);

  // Scroll to bottom only when appropriate (user is near bottom or just sent a message)
  useEffect(() => {
    if (shouldAutoScrollRef.current) {
      const container = messagesContainerRef.current;
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth',
        });
      }
    }
  }, [messages]);

  // Load conversations
  const loadConversations = useCallback(async () => {
    setIsLoadingConversations(true);
    try {
      const convos = await agentService.listConversations();
      setConversations(convos);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setIsLoadingConversations(false);
    }
  }, []);

  // Load available agents on mount
  const loadAgents = useCallback(async () => {
    setIsLoadingAgents(true);
    try {
      const agentList = await agentService.listAgents();
      setAgents(agentList);
      // Select first agent by default if available
      if (agentList.length > 0 && !selectedAgent) {
        setSelectedAgent(agentList[0]);
        setSystemPrompt(agentList[0].system_prompt);
      }
    } catch (err) {
      console.error('Failed to load agents:', err);
      setAgents([]);
    } finally {
      setIsLoadingAgents(false);
    }
  }, [selectedAgent]);

  // Load available MCP servers on mount
  const loadMCPServers = useCallback(async () => {
    setIsLoadingServers(true);
    try {
      const servers = await agentService.listMCPServers();
      setMcpServers(servers);
      // Enable all connected servers by default
      const connectedUrls = servers
        .filter(s => s.status === 'connected')
        .map(s => s.url);
      setEnabledServers(new Set(connectedUrls));
    } catch (err) {
      console.error('Failed to load MCP servers:', err);
      setMcpServers([]);
    } finally {
      setIsLoadingServers(false);
    }
  }, []);

  // Load tools when enabled servers change
  const loadTools = useCallback(async () => {
    // Use agent's servers when in agent mode, otherwise use manually selected servers
    const serversToUse = configMode === 'agent' ? agentEnabledServers : enabledServers;

    if (serversToUse.size === 0) {
      setTools([]);
      return;
    }
    setIsLoadingTools(true);
    try {
      // In agent mode, pass the agent ID so the backend can read the full
      // mcp_servers config (including dict entries with auth headers) from
      // the database instead of relying on plain URL strings.
      const serverUrls = Array.from(serversToUse);
      const agentId = configMode === 'agent' ? selectedAgent?.id : undefined;
      const toolList = await agentService.listTools(serverUrls, agentId);
      setTools(toolList);
    } catch (err) {
      console.error('Failed to load tools:', err);
      setTools([]);
    } finally {
      setIsLoadingTools(false);
    }
  }, [configMode, enabledServers, agentEnabledServers, selectedAgent]);

  // Sync agent's MCP servers to local state when agent changes
  useEffect(() => {
    if (selectedAgent) {
      setAgentEnabledServers(new Set(getMCPServerUrls(selectedAgent.mcp_servers)));
      setSystemPrompt(selectedAgent.system_prompt);
      // Set model to match agent's default
      const agentModel = MODEL_OPTIONS.find(
        o => o.provider === selectedAgent.default_model_provider && o.model === selectedAgent.default_model_name
      );
      if (agentModel) {
        setSelectedModel(agentModel);
      }
    }
  }, [selectedAgent]);

  // Check if agent has unsaved changes
  const hasUnsavedChanges = useCallback((): boolean => {
    if (!selectedAgent || configMode !== 'agent') return false;

    // Check system prompt
    if (systemPrompt !== selectedAgent.system_prompt) return true;

    // Check MCP servers
    const currentServers = Array.from(agentEnabledServers).sort();
    const savedServers = getMCPServerUrls(selectedAgent.mcp_servers).sort();
    if (currentServers.length !== savedServers.length) return true;
    if (!currentServers.every((s, i) => s === savedServers[i])) return true;

    // Check model
    if (selectedModel.provider !== selectedAgent.default_model_provider) return true;
    if (selectedModel.model !== selectedAgent.default_model_name) return true;

    return false;
  }, [selectedAgent, configMode, systemPrompt, agentEnabledServers, selectedModel]);

  // Save agent configuration
  const handleSaveAgent = async () => {
    if (!selectedAgent) return;

    setIsSavingAgent(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const updates: AgentUpdateRequest = {
        system_prompt: systemPrompt,
        mcp_servers: Array.from(agentEnabledServers),
        default_model_provider: selectedModel.provider,
        default_model_name: selectedModel.model,
      };

      const updatedAgent = await agentService.updateAgent(selectedAgent.id, updates);

      // Update local state
      setSelectedAgent(updatedAgent);
      setAgents(prev => prev.map(a => a.id === updatedAgent.id ? updatedAgent : a));
      setSaveSuccess(true);

      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save agent configuration');
    } finally {
      setIsSavingAgent(false);
    }
  };

  // Toggle MCP server for agent configuration
  const handleToggleAgentServer = (serverUrl: string) => {
    setAgentEnabledServers(prev => {
      const next = new Set(prev);
      if (next.has(serverUrl)) {
        next.delete(serverUrl);
      } else {
        next.add(serverUrl);
      }
      return next;
    });
  };

  // Build history items from messages for the history modal
  const buildHistoryFromMessages = useCallback((msgs: ChatMessage[]) => {
    const items: HistoryItem[] = [];

    // Find the first tool call timestamp to use as the request start time
    // This gives us a reference point for elapsed time calculations
    let requestStartTime: number | null = null;
    msgs.forEach((msg) => {
      if (msg.metadata?.tool_calls) {
        msg.metadata.tool_calls.forEach((tc: ToolCallDetail) => {
          if (tc.started_at) {
            const startTime = new Date(tc.started_at).getTime();
            if (requestStartTime === null || startTime < requestStartTime) {
              requestStartTime = startTime;
            }
          }
        });
      }
    });

    msgs.forEach((msg) => {
      // Add the message itself
      // For user messages, no elapsed time (they start the request)
      // For assistant messages, elapsed time is the total latency
      const assistantElapsedEnd = msg.role === 'assistant' && msg.metadata?.latency_ms
        ? msg.metadata.latency_ms
        : undefined;

      items.push({
        type: msg.role as 'user' | 'assistant',
        content: msg.content,
        timestamp: msg.created_at,
        elapsedStartMs: msg.role === 'user' ? 0 : undefined,
        elapsedEndMs: assistantElapsedEnd,
        metadata: msg.role === 'assistant' ? {
          tokens: msg.metadata?.tokens_used,
          cost: msg.metadata?.cost_usd,
          latency: msg.metadata?.latency_ms,
        } : undefined,
        requestMetadata: msg.metadata?.request_metadata,
      });

      // Add each LLM call as separate items (before tool calls)
      if (msg.metadata?.llm_calls) {
        msg.metadata.llm_calls.forEach((lc: LLMCallDetail, index: number) => {
          items.push({
            type: 'llm_call',
            content: `LLM Call to ${lc.model}`,
            timestamp: lc.started_at || msg.created_at,
            metadata: {
              llm_model: lc.model,
              llm_input_tokens: lc.input_tokens,
              llm_output_tokens: lc.output_tokens,
              llm_total_tokens: lc.total_tokens,
              llm_cost: lc.cost_usd,
              llm_latency: lc.latency_ms,
              llm_error: lc.error,
              llm_call_index: index,
            },
          });
        });
      }

      // Add each tool call as separate items with their own elapsed times
      if (msg.metadata?.tool_calls) {
        msg.metadata.tool_calls.forEach((tc: ToolCallDetail) => {
          // Calculate elapsed times relative to request start
          let toolStartElapsed: number | undefined;
          let toolEndElapsed: number | undefined;

          if (requestStartTime !== null) {
            if (tc.started_at) {
              toolStartElapsed = new Date(tc.started_at).getTime() - requestStartTime;
            }
            if (tc.completed_at) {
              toolEndElapsed = new Date(tc.completed_at).getTime() - requestStartTime;
            }
          }

          items.push({
            type: 'tool_call',
            content: JSON.stringify(tc.arguments, null, 2),
            timestamp: tc.started_at || msg.created_at,
            elapsedStartMs: toolStartElapsed,
            elapsedEndMs: toolStartElapsed, // Tool call itself is instantaneous
            metadata: {
              tool_name: tc.tool_name,
              tool_args: tc.arguments,
              duration_ms: tc.duration_ms ?? undefined,
            },
          });
          if (tc.result) {
            items.push({
              type: 'tool_result',
              content: tc.result,
              timestamp: tc.completed_at || msg.created_at,
              elapsedStartMs: toolStartElapsed,
              elapsedEndMs: toolEndElapsed,
              metadata: {
                tool_name: tc.tool_name,
                duration_ms: tc.duration_ms ?? undefined,
              },
            });
          }
        });
      }
    });
    setHistoryItems(items);
  }, []);

  // Open history modal and scroll to a specific message
  const openHistoryForMessage = useCallback((messageIndex: number) => {
    buildHistoryFromMessages(messages);
    // Calculate which history item corresponds to this message
    let historyIndex = 0;
    for (let i = 0; i < messageIndex; i++) {
      historyIndex += 1; // The message itself
      const msgToolCalls = messages[i].metadata?.tool_calls || [];
      msgToolCalls.forEach((tc: ToolCallDetail) => {
        historyIndex += 1; // tool_call item
        if (tc.result) historyIndex += 1; // tool_result item
      });
    }
    setHighlightedHistoryIndex(historyIndex);
    setShowHistoryModal(true);
  }, [messages, buildHistoryFromMessages]);

  // Open history modal and scroll to a specific tool call by its exact history index
  const openHistoryForTool = useCallback((historyIndex: number) => {
    buildHistoryFromMessages(messages);
    setHighlightedHistoryIndex(historyIndex);
    setShowHistoryModal(true);
  }, [messages, buildHistoryFromMessages]);

  // Close history modal
  const closeHistoryModal = useCallback(() => {
    setShowHistoryModal(false);
    setHighlightedHistoryIndex(null);
  }, []);

  // Load conversations, agents, and MCP servers on mount
  useEffect(() => {
    loadConversations();
    loadAgents();
    loadMCPServers();
  }, [loadConversations, loadAgents, loadMCPServers]);

  // Load tools when enabled servers change or config mode changes
  useEffect(() => {
    const serversToUse = configMode === 'agent' ? agentEnabledServers : enabledServers;
    if (serversToUse.size > 0) {
      loadTools();
    } else {
      setTools([]);
    }
  }, [loadTools, enabledServers, agentEnabledServers, configMode]);

  // Send message
  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: inputValue.trim(),
      created_at: new Date().toISOString(),
    };

    // Enable auto-scroll when user sends a message
    shouldAutoScrollRef.current = true;

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setError(null);
    setIsLoading(true);

    // Add a loading placeholder
    const loadingMessage: ChatMessage = {
      role: 'assistant',
      content: '',
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, loadingMessage]);

    try {
      // Build request based on config mode
      let requestConfig: {
        message: string;
        conversation_id?: string;
        agent_id?: string;
        mcp_servers?: string[];
        model_provider?: string;
        model_name?: string;
        conversation_history?: typeof messages;
      };

      if (configMode === 'agent' && selectedAgent) {
        // Use agent configuration with user's enabled server selection
        const enabledAgentServerUrls = Array.from(agentEnabledServers);
        requestConfig = {
          message: userMessage.content,
          conversation_id: conversationId || undefined,
          agent_id: selectedAgent.id,
          mcp_servers: enabledAgentServerUrls.length > 0 ? enabledAgentServerUrls : undefined,
          model_provider: selectedModel.provider,
          model_name: selectedModel.model,
          conversation_history: messages,
        };
      } else {
        // Use manual MCP server configuration
        const enabledServerUrls = Array.from(enabledServers);
        requestConfig = {
          message: userMessage.content,
          conversation_id: conversationId || undefined,
          mcp_servers: enabledServerUrls.length > 0 ? enabledServerUrls : undefined,
          model_provider: selectedModel.provider,
          model_name: selectedModel.model,
          conversation_history: messages,
        };
      }

      const response: AgentResponse = await agentService.sendMessage(requestConfig);

      // Update conversation ID if new
      if (response.conversation_id && !conversationId) {
        setConversationId(response.conversation_id);
      }

      // Replace loading message with actual response
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response.message,
        created_at: response.created_at || new Date().toISOString(),
        metadata: {
          model_provider: response.model_provider,
          model_name: response.model_name,
          tokens_used: response.tokens_used ?? undefined,
          cost_usd: response.cost_usd ?? undefined,
          latency_ms: response.latency_ms ?? undefined,
          tool_calls: response.tool_calls ?? undefined,
          llm_calls: response.llm_calls ?? undefined,
          reasoning_trace: response.reasoning_trace,
          request_metadata: response.request_metadata ?? undefined,
        },
      };

      setMessages(prev => [...prev.slice(0, -1), assistantMessage]);

      // Refresh conversations list
      loadConversations();
    } catch (err) {
      // Remove loading message on error
      setMessages(prev => prev.slice(0, -1));
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Start new conversation
  const handleNewConversation = () => {
    setMessages([]);
    setConversationId(null);
    setError(null);
    inputRef.current?.focus();
  };

  // Load existing conversation
  const handleLoadConversation = async (id: string) => {
    setIsLoading(true);
    try {
      const convo = await agentService.getConversation(id);
      setMessages(convo.messages);
      setConversationId(id);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load conversation');
    } finally {
      setIsLoading(false);
    }
  };

  // Delete conversation
  const handleDeleteConversation = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Delete this conversation?')) return;

    try {
      await agentService.deleteConversation(id);
      if (conversationId === id) {
        handleNewConversation();
      }
      loadConversations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete conversation');
    }
  };

  // Toggle MCP server
  const handleToggleServer = (serverUrl: string) => {
    setEnabledServers(prev => {
      const next = new Set(prev);
      if (next.has(serverUrl)) {
        next.delete(serverUrl);
      } else {
        next.add(serverUrl);
      }
      return next;
    });
  };

  // Format date for conversation list
  const formatConvoDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return formatDate(dateStr);
  };

  // Copy conversation ID to clipboard
  const handleCopyConversationId = useCallback(async () => {
    if (!conversationId) return;
    try {
      await navigator.clipboard.writeText(conversationId);
      setCopiedConversationId(true);
      setTimeout(() => setCopiedConversationId(false), 2000);
    } catch (err) {
      console.error('Failed to copy conversation ID:', err);
    }
  }, [conversationId]);

  return (
    <div className={styles.container}>
      {/* Sidebar */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <h2>Agent Chat</h2>
          <Button
            variant="primary"
            size="sm"
            onClick={handleNewConversation}
            leftIcon={<PlusIcon />}
          >
            New Chat
          </Button>
        </div>

        {/* Sidebar Tabs */}
        <div className={styles.sidebarTabs}>
          <button
            className={`${styles.sidebarTab} ${sidebarTab === 'config' ? styles.activeTab : ''}`}
            onClick={() => setSidebarTab('config')}
          >
            <SettingsIcon />
            Config
          </button>
          <button
            className={`${styles.sidebarTab} ${sidebarTab === 'conversations' ? styles.activeTab : ''}`}
            onClick={() => setSidebarTab('conversations')}
          >
            <ChatIcon />
            History
          </button>
          <button
            className={`${styles.sidebarTab} ${sidebarTab === 'tools' ? styles.activeTab : ''}`}
            onClick={() => setSidebarTab('tools')}
          >
            <ToolIcon />
            Tools
          </button>
        </div>

        {/* Tab Content */}
        <div className={styles.sidebarContent}>
          {sidebarTab === 'config' && (
            <div className={styles.configSection}>
              {/* Configuration Mode Toggle */}
              <div className={styles.configGroup}>
                <label className={styles.configLabel}>Configuration Mode</label>
                <div className={styles.configModeToggle}>
                  <button
                    className={`${styles.modeButton} ${configMode === 'agent' ? styles.modeButtonActive : ''}`}
                    onClick={() => setConfigMode('agent')}
                  >
                    <RobotIcon />
                    Agent
                  </button>
                  <button
                    className={`${styles.modeButton} ${configMode === 'manual' ? styles.modeButtonActive : ''}`}
                    onClick={() => setConfigMode('manual')}
                  >
                    <ToolIcon />
                    Manual
                  </button>
                </div>
              </div>

              {/* Agent Selection (when in agent mode) */}
              {configMode === 'agent' && (
                <>
                  <div className={styles.configGroup}>
                    <label className={styles.configLabel}>
                      <RobotIcon />
                      Agent
                    </label>
                    {isLoadingAgents ? (
                      <div className={styles.loading}>
                        <SpinnerIcon />
                        <span>Loading agents...</span>
                      </div>
                    ) : agents.length > 0 ? (
                      <select
                        className={styles.configSelect}
                        value={selectedAgent?.id || ''}
                        onChange={(e) => {
                          const agent = agents.find(a => a.id === e.target.value);
                          setSelectedAgent(agent || null);
                        }}
                      >
                        {agents.map(agent => (
                          <option key={agent.id} value={agent.id}>
                            {agent.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <p className={styles.configHint}>
                        No agents available. Create one in the admin panel.
                      </p>
                    )}

                    {/* Agent description */}
                    {selectedAgent?.description && (
                      <p className={styles.agentDescription}>{selectedAgent.description}</p>
                    )}
                  </div>

                  {/* MCP Servers for Agent */}
                  {selectedAgent && (
                    <div className={styles.configGroup}>
                      <label className={styles.configLabel}>
                        <ToolIcon />
                        MCP Servers
                      </label>
                      {isLoadingServers ? (
                        <div className={styles.loading}>
                          <SpinnerIcon />
                          <span>Loading MCP servers...</span>
                        </div>
                      ) : mcpServers.length > 0 ? (
                        <div className={styles.mcpServerList}>
                          {mcpServers.map(server => (
                            <label
                              key={server.url}
                              className={`${styles.mcpServerItem} ${server.status === 'error' ? styles.mcpServerError : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={agentEnabledServers.has(server.url)}
                                onChange={() => handleToggleAgentServer(server.url)}
                                disabled={server.status === 'error'}
                              />
                              <div className={styles.mcpServerInfo}>
                                <div className={styles.mcpServerHeader}>
                                  <span className={styles.mcpServerName}>{server.name}</span>
                                  <StatusBadge
                                    variant={server.status === 'connected' ? 'success' : server.status === 'error' ? 'error' : 'warning'}
                                  >
                                    {server.status}
                                  </StatusBadge>
                                </div>
                                <span className={styles.mcpServerDescription}>{server.description}</span>
                                <span className={styles.mcpServerTools}>
                                  {server.tool_count} tool{server.tool_count !== 1 ? 's' : ''} available
                                </span>
                              </div>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <p className={styles.configHint}>
                          No MCP servers available.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Save Button for Agent Config */}
                  {selectedAgent && (
                    <div className={styles.configGroup}>
                      <div className={styles.saveSection}>
                        <Button
                          variant={hasUnsavedChanges() ? 'primary' : 'secondary'}
                          size="sm"
                          onClick={handleSaveAgent}
                          disabled={!hasUnsavedChanges() || isSavingAgent}
                          leftIcon={isSavingAgent ? <SpinnerIcon /> : <SaveIcon />}
                        >
                          {isSavingAgent ? 'Saving...' : 'Save Configuration'}
                        </Button>
                        {hasUnsavedChanges() && (
                          <span className={styles.unsavedIndicator}>Unsaved changes</span>
                        )}
                        {saveSuccess && (
                          <span className={styles.saveSuccess}>Saved!</span>
                        )}
                        {saveError && (
                          <span className={styles.saveError}>{saveError}</span>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Model Selection */}
              <div className={styles.configGroup}>
                <label className={styles.configLabel}>Model</label>
                <select
                  className={styles.configSelect}
                  value={`${selectedModel.provider}:${selectedModel.model}`}
                  onChange={(e) => {
                    const [provider, model] = e.target.value.split(':');
                    const option = MODEL_OPTIONS.find(o => o.provider === provider && o.model === model);
                    if (option) setSelectedModel(option);
                  }}
                >
                  {MODEL_OPTIONS.map(option => (
                    <option key={`${option.provider}:${option.model}`} value={`${option.provider}:${option.model}`}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* System Prompt */}
              <div className={styles.configGroup}>
                <label className={styles.configLabel}>System Prompt</label>
                <button
                  className={styles.editPromptButton}
                  onClick={() => {
                    setTempSystemPrompt(systemPrompt);
                    setIsSystemPromptModalOpen(true);
                  }}
                >
                  <EditIcon />
                  <span className={styles.editPromptButtonText}>
                    {systemPrompt ? systemPrompt.substring(0, 60) + (systemPrompt.length > 60 ? '...' : '') : 'Click to edit...'}
                  </span>
                </button>
              </div>

              {/* MCP Servers (when in manual mode) */}
              {configMode === 'manual' && (
                <div className={styles.configGroup}>
                  <label className={styles.configLabel}>
                    <ToolIcon />
                    MCP Servers
                  </label>
                  {isLoadingServers ? (
                    <div className={styles.loading}>
                      <SpinnerIcon />
                      <span>Loading MCP servers...</span>
                    </div>
                  ) : mcpServers.length > 0 ? (
                    <div className={styles.mcpServerList}>
                      {mcpServers.map(server => (
                        <label
                          key={server.url}
                          className={`${styles.mcpServerItem} ${server.status === 'error' ? styles.mcpServerError : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={enabledServers.has(server.url)}
                            onChange={() => handleToggleServer(server.url)}
                            disabled={server.status === 'error'}
                          />
                          <div className={styles.mcpServerInfo}>
                            <div className={styles.mcpServerHeader}>
                              <span className={styles.mcpServerName}>{server.name}</span>
                              <StatusBadge
                                variant={server.status === 'connected' ? 'success' : server.status === 'error' ? 'error' : 'warning'}
                              >
                                {server.status}
                              </StatusBadge>
                            </div>
                            <span className={styles.mcpServerDescription}>{server.description}</span>
                            <span className={styles.mcpServerTools}>
                              {server.tool_count} tool{server.tool_count !== 1 ? 's' : ''} available
                            </span>
                          </div>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className={styles.configHint}>
                      No MCP servers configured.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {sidebarTab === 'conversations' && (
            <div className={styles.conversationList}>
              {isLoadingConversations ? (
                <div className={styles.loading}>
                  <SpinnerIcon />
                  <span>Loading...</span>
                </div>
              ) : conversations.length === 0 ? (
                <p className={styles.emptyState}>No conversations yet.</p>
              ) : (
                conversations.map(convo => (
                  <div
                    key={convo.id}
                    className={`${styles.conversationItem} ${convo.id === conversationId ? styles.activeConvo : ''}`}
                    onClick={() => handleLoadConversation(convo.id)}
                  >
                    <div className={styles.convoInfo}>
                      <span className={styles.convoTitle}>{convo.title}</span>
                      <span className={styles.convoMeta}>
                        {convo.message_count} messages &middot; {formatConvoDate(convo.updated_at)}
                      </span>
                    </div>
                    <button
                      className={styles.deleteButton}
                      onClick={(e) => handleDeleteConversation(convo.id, e)}
                      title="Delete conversation"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {sidebarTab === 'tools' && (
            <div className={styles.toolsList}>
              {enabledServers.size === 0 ? (
                <p className={styles.emptyState}>
                  Enable MCP servers in the Config tab to see available tools.
                </p>
              ) : isLoadingTools ? (
                <div className={styles.loading}>
                  <SpinnerIcon />
                  <span>Loading tools...</span>
                </div>
              ) : tools.length === 0 ? (
                <p className={styles.emptyState}>No tools available from enabled servers.</p>
              ) : (
                tools.map((tool, idx) => (
                  <div key={`${tool.mcp_server}-${tool.name}-${idx}`} className={styles.toolItem}>
                    <div className={styles.toolHeader}>
                      <ToolIcon />
                      <span className={styles.toolName}>{tool.name}</span>
                    </div>
                    <p className={styles.toolDescription}>{tool.description}</p>
                    <span className={styles.toolServer}>{tool.mcp_server}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </aside>

      {/* Main Chat Area */}
      <main className={styles.main}>
        {/* Messages */}
        <div
          ref={messagesContainerRef}
          onScroll={handleMessagesScroll}
          className={styles.messagesContainer}
        >
          {messages.length === 0 ? (
            <div className={styles.emptyChat}>
              <ChatIcon />
              <h3>Start a Conversation</h3>
              <p>Send a message to start chatting with the AI agent.</p>
              {(() => {
                const activeServers = configMode === 'agent' ? agentEnabledServers : enabledServers;
                if (activeServers.size > 0) {
                  return (
                    <p className={styles.emptyChatHint}>
                      {tools.length} tool{tools.length !== 1 ? 's' : ''} available from {activeServers.size} MCP server{activeServers.size !== 1 ? 's' : ''}
                    </p>
                  );
                }
                return null;
              })()}
            </div>
          ) : (
            <div className={styles.messagesList}>
              {messages.map((msg, idx) => (
                <MessageBubble
                  key={`${msg.created_at}-${idx}`}
                  message={msg}
                  messageIndex={idx}
                  messages={messages}
                  isLoading={isLoading && idx === messages.length - 1 && msg.role === 'assistant' && !msg.content}
                  onOpenHistory={openHistoryForMessage}
                  onOpenToolHistory={openHistoryForTool}
                />
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Error display */}
        {error && (
          <div className={styles.errorBar}>
            <span>{error}</span>
            <button onClick={() => setError(null)}>&times;</button>
          </div>
        )}

        {/* Input Area */}
        <div className={styles.inputArea}>
          <div className={styles.inputWrapper}>
            <textarea
              ref={inputRef}
              className={styles.messageInput}
              placeholder="Type your message..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={isLoading}
            />
            <Button
              variant="primary"
              size="sm"
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              className={styles.sendButton}
            >
              {isLoading ? <SpinnerIcon /> : <SendIcon />}
            </Button>
          </div>
          <div className={styles.inputMeta}>
            <span>{selectedModel.label}</span>
            <div className={styles.inputMetaRight}>
              {conversationId && (
                <button
                  className={styles.conversationIdButton}
                  onClick={handleCopyConversationId}
                  title="Click to copy conversation ID"
                >
                  {copiedConversationId ? <CheckIcon /> : <CopyIcon />}
                  <span className={styles.conversationIdText}>
                    {conversationId.slice(0, 8)}...
                  </span>
                  {copiedConversationId && <span className={styles.copiedText}>Copied!</span>}
                </button>
              )}
              {configMode === 'agent' && selectedAgent ? (
                <span>Agent: {selectedAgent.name} &middot; {agentEnabledServers.size} server{agentEnabledServers.size !== 1 ? 's' : ''} &middot; {tools.length} tool{tools.length !== 1 ? 's' : ''}</span>
              ) : enabledServers.size > 0 ? (
                <span>{enabledServers.size} MCP server{enabledServers.size !== 1 ? 's' : ''} &middot; {tools.length} tool{tools.length !== 1 ? 's' : ''}</span>
              ) : null}
            </div>
          </div>
        </div>
      </main>

      {/* System Prompt Modal */}
      {isSystemPromptModalOpen && (
        <div className={styles.modalOverlay} onClick={() => setIsSystemPromptModalOpen(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Edit System Prompt</h3>
              <button
                className={styles.modalCloseButton}
                onClick={() => setIsSystemPromptModalOpen(false)}
              >
                <CloseIcon />
              </button>
            </div>
            <div className={styles.modalBody}>
              <textarea
                className={styles.modalTextarea}
                value={tempSystemPrompt}
                onChange={(e) => setTempSystemPrompt(e.target.value)}
                placeholder="Enter the system prompt for this agent..."
                autoFocus
              />
            </div>
            <div className={styles.modalFooter}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setIsSystemPromptModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setSystemPrompt(tempSystemPrompt);
                  setIsSystemPromptModalOpen(false);
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      <HistoryModal
        isOpen={showHistoryModal}
        onClose={closeHistoryModal}
        historyItems={historyItems}
        highlightedIndex={highlightedHistoryIndex}
      />
    </div>
  );
}
